// BeforeInstallPrompt — captured so we can trigger it programmatically
let _deferredPrompt: any = null

function _isStandaloneOrInstalled(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  )
}

// Pages that are transient (OAuth flows, callbacks) should not defer the install prompt
// because the page navigates away before prompt() can be called, triggering a browser warning.
const TRANSIENT_PATHS = ['/auth/oauth/callback', '/auth/']

function _isTransientPage(): boolean {
  return TRANSIENT_PATHS.some((p) => window.location.pathname.startsWith(p))
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Don't intercept if already running as installed PWA or on transient pages
    if (_isStandaloneOrInstalled() || _isTransientPage()) return

    e.preventDefault()
    _deferredPrompt = e
    window.dispatchEvent(new CustomEvent('pwa-installable'))
  })

  window.addEventListener('appinstalled', () => {
    _deferredPrompt = null
    window.dispatchEvent(new CustomEvent('pwa-installed'))
  })

  // If the page is hidden/unloaded without the user having triggered prompt(),
  // release the deferred prompt so the browser doesn't log the banner warning.
  const releaseDeferredPrompt = () => {
    if (_deferredPrompt && document.visibilityState === 'hidden') {
      _deferredPrompt = null
    }
  }
  document.addEventListener('visibilitychange', releaseDeferredPrompt)
}

export function getInstallPrompt() { return _deferredPrompt }
export function clearInstallPrompt() { _deferredPrompt = null }

export async function triggerInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!_deferredPrompt) return 'unavailable'
  _deferredPrompt.prompt()
  const { outcome } = await _deferredPrompt.userChoice
  _deferredPrompt = null
  return outcome as 'accepted' | 'dismissed'
}

export async function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js')
      if (import.meta.env.DEV) console.log('SW registered:', reg.scope)
      return reg
    } catch (err) {
      console.warn('SW registration failed:', err)
    }
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw     = window.atob(base64)
  const arr     = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
  return arr
}

export async function subscribeWebPush(vapidPublicKey: string): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null

  const perm = await Notification.requestPermission()
  if (perm !== 'granted') return null

  try {
    const reg = await navigator.serviceWorker.ready
    const existing = await reg.pushManager.getSubscription()
    if (existing) return existing

    return await reg.pushManager.subscribe({
      userVisibleOnly:      true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    })
  } catch (err) {
    console.warn('Web push subscribe failed:', err)
    return null
  }
}

export async function unsubscribeWebPush(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false
  try {
    const reg  = await navigator.serviceWorker.ready
    const sub  = await reg.pushManager.getSubscription()
    if (!sub) return false
    return sub.unsubscribe()
  } catch {
    return false
  }
}

export function pushNotificationsSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export async function saveDiveOffline(dive: Record<string, unknown>, token: string) {
  const id = `offline_${Date.now()}_${Math.random().toString(36).slice(2)}`
  const request = indexedDB.open('edive-offline', 1)
  request.onsuccess = (e) => {
    const db = (e.target as IDBOpenDBRequest).result
    const tx = db.transaction('pending-dives', 'readwrite')
    tx.objectStore('pending-dives').add({ ...dive, _offlineId: id, _token: token })
  }
  return id
}

export function isOnline(): boolean {
  return navigator.onLine
}
